/* global interfaceConfig */

import React from 'react';
import Spinner from '@atlaskit/spinner';

import { isMobileBrowser } from '../../base/environment/utils';
import { translate, translateToHTML } from '../../base/i18n';
import { Icon, IconWarning } from '../../base/icons';
import { Watermarks } from '../../base/react';
import { connect } from '../../base/redux/functions';
import { CalendarList } from '../../calendar-sync';
import { RecentList } from '../../recent-list';
import { SettingsButton, SETTINGS_TABS } from '../../settings';

import { AbstractWelcomePage, _mapStateToProps } from './AbstractWelcomePage';
import Tabs from './Tabs';
import LoginPage from './LoginPage';
import { jitsiLocalStorage } from '@jitsi/js-utils';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import '../../../../css/loginStyle.css';
import { doSSOLogin, getJwt } from '../actions';
import { UserManager } from 'oidc-client';

/**
 * The pattern used to validate room name.
 *
 * @type {string}
 */
export const ROOM_NAME_VALIDATE_PATTERN_STR = '^[^?&:\u0022\u0027%#]+$';

/**
 * The Web container rendering the welcome page.
 *
 * @augments AbstractWelcomePage
 */
class WelcomePage extends AbstractWelcomePage {
    /**
     * Default values for {@code WelcomePage} component's properties.
     *
     * @static
     */
    static defaultProps = {
        _room: ''
    };

    userManager: UserManager | null | undefined;

    /**
     * Initializes a new WelcomePage instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);

        this.state = {
            ...this.state,

            generateRoomnames:
                interfaceConfig.GENERATE_ROOMNAMES_ON_WELCOME_PAGE,
            selectedTab: 0,
            showLoginPage: false,
            loginButtonText: this.isUserLoggedIn()
                ? this.props.t('toolbar.logout')
                : this.props.t('toolbar.login')
        };

        /**
         * The HTML Element used as the container for additional content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentRef = null;

        this._roomInputRef = null;

        /**
         * The HTML Element used as the container for additional toolbar content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentRef = null;

        this._additionalCardRef = null;

        /**
         * The template to use as the additional card displayed near the main one.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalCardTemplate = document.getElementById(
            'welcome-page-additional-card-template');

        /**
         * The template to use as the main content for the welcome page. If
         * not found then only the welcome page head will display.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentTemplate = document.getElementById(
            'welcome-page-additional-content-template');

        /**
         * The template to use as the additional content for the welcome page header toolbar.
         * If not found then only the settings icon will be displayed.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentTemplate = document.getElementById(
            'settings-toolbar-additional-content-template'
        );

        // Bind event handlers so they are only bound once per instance.
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onRoomChange = this._onRoomChange.bind(this);
        this._setAdditionalCardRef = this._setAdditionalCardRef.bind(this);
        this._setAdditionalContentRef =
            this._setAdditionalContentRef.bind(this);
        this._setRoomInputRef = this._setRoomInputRef.bind(this);
        this._setAdditionalToolbarContentRef =
            this._setAdditionalToolbarContentRef.bind(this);
        this._onTabSelected = this._onTabSelected.bind(this);
        this.onLoginButtonClick = this.onLoginButtonClick.bind(this);
        this.isUserLoggedIn = this.isUserLoggedIn.bind(this);
        this.onLogoutClick = this.onLogoutClick.bind(this);
        this.loginSuccessCallback = this.loginSuccessCallback.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.getUserName = this.getUserName.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
        this.handleSSOLogout = this.handleSSOLogout.bind(this);
        this.onSSOLogoutClick = this.onSSOLogoutClick.bind(this);
        this.onSSOButtonClick = this.onSSOButtonClick.bind(this);
        this.handleSSOLogin = this.handleSSOLogin.bind(this);
        this.toggleMenu = this.toggleMenu.bind(this);
        this.onMenuClick = this.onMenuClick.bind(this);
        this.onUserIconClick = this.onUserIconClick.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidMount()}. Invoked
     * immediately after this component is mounted.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {

        const {_ssoConf} = this.props;

        super.componentDidMount();

        const ssoConfig = {
            authority: _ssoConf.authority,
            client_id: _ssoConf.client_id,
            redirect_uri: _ssoConf.redirect_uri,
            response_type: _ssoConf.response_type,
            scope: _ssoConf.scope,
            post_logout_redirect_uri: _ssoConf.post_logout_redirect_uri,
        };

        this.userManager = new UserManager(ssoConfig);

        this.handleSSOLogin();
        this.handleSSOLogout();
        document.body.classList.add('welcome-page');
        document.title = interfaceConfig.APP_NAME;

        if (this.state.generateRoomnames) {
            this._updateRoomname();
        }

        if (this._shouldShowAdditionalContent()) {
            this._additionalContentRef.appendChild(
                this._additionalContentTemplate.content.cloneNode(true));
        }

        if (this._shouldShowAdditionalToolbarContent()) {
            this._additionalToolbarContentRef.appendChild(
                this._additionalToolbarContentTemplate.content.cloneNode(true)
            );
        }

        if (this._shouldShowAdditionalCard()) {
            this._additionalCardRef.appendChild(
                this._additionalCardTemplate.content.cloneNode(true)
            );
        }
    }

    /**
     * Removes the classname used for custom styling of the welcome page.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        super.componentWillUnmount();

        document.body.classList.remove('welcome-page');
    }

    handleSSOLogin() {
        const { dispatch, _ssoConf } = this.props;
        this.userManager.signinRedirectCallback().then(() => {
            this.userManager.getUser().then((user) => {
                if (user) {
                    const roomName = jitsiLocalStorage.getItem("bbbssoroomname");
                    // console.log(user);
                    let requestBody = {
                        sso_token: user.access_token,
                        room_name: roomName || '',
                        ssoConfRedirect_uri: _ssoConf.redirect_uri
                    };
                    // console.log(requestBody);
                    // console.log(JSON.stringify(user));
                    dispatch(doSSOLogin(requestBody));
                    this.loginSuccessCallback();
                }
            });
        }).catch(function (e) {
            console.error(e);
        });
    }

    handleSSOLogout() {
        this.userManager.signoutRedirectCallback().then(() => {
            // this.onLogoutClick();
        }).catch((e) => {
            console.error(e);
        })
    }

    onLoginButtonClick() {
        this.setState({ showLoginPage: true });
    }

    onSSOButtonClick() {
        // const ssoConfig = {
        //     authority: "https://bbbsso-uat.azurewebsites.net",
        //     client_id: "bsm_php",
        //     redirect_uri: "https://blueskymeet.com",
        //     response_type: "code",
        //     scope: "openid profile email",
        //     post_logout_redirect_uri: "https://blueskymeet.com",
        // };

        // //     authority: "https://blueskymeet.com/",
        // // client_id: "bsm_php",
        // // redirect_uri: "https://blueskymeet.com/oauthredirect",
        // // response_type: "id_token token",
        // // scope:"openid profile api1",
        // // post_logout_redirect_uri : "https://blueskymeet.com",

        // const mrg = new UserManager(ssoConfig);
        this.userManager.signinRedirect().then(function () {
            console.log('signinRedirect ok');
        }).catch(function (err) {
            console.log('signinRedirect error:', err);
        });
    }

    isUserLoggedIn() {
        let user_token = jitsiLocalStorage.getItem('user_token');
        if (user_token) {
            return true;
        }
        return false;
    }

    onLogoutClick() {
        jitsiLocalStorage.removeItem('user_token');
        jitsiLocalStorage.removeItem('username');
        jitsiLocalStorage.removeItem('sso_token');
        this.setState({ loginButtonText: this.props.t('toolbar.login') });
    }

    onSSOLogoutClick() {
        this.userManager.signoutRedirect().then(() => {
            this.onLogoutClick();
            console.log("user log out initialized");
        });
    }

    loginSuccessCallback() {
        this.setState({
            loginButtonText: this.props.t('toolbar.logout'),
            showLoginPage: false,
        });
    }

    onModalClose() {
        this.setState({ showLoginPage: false });
    }
    getUserName() {
        let username = jitsiLocalStorage.getItem('username');
        if (username) return username;
        return '';
    }

    toggleMenu() {
        $(".nav-main").slideToggle();
        $(".nav-main").css("display", "inline-flex");
    }

    onMenuClick() {
        $(".wel-menu-list").slideToggle();
        $(".wel-menu-list").css("display", "block");
    }

    onUserIconClick() {
        $(".user-action").slideToggle();
        $(".user-action").css("display", "block");
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const { _moderatedRoomServiceUrl, t, _settings } = this.props;
        const { APP_NAME, DEFAULT_WELCOME_PAGE_LOGO_URL, DISPLAY_WELCOME_FOOTER } = interfaceConfig;
        const showAdditionalCard = this._shouldShowAdditionalCard();
        const showAdditionalContent = this._shouldShowAdditionalContent();
        const showAdditionalToolbarContent = this._shouldShowAdditionalToolbarContent();
        const contentClassName = showAdditionalContent ? 'with-content' : 'without-content';
        const footerClassName = DISPLAY_WELCOME_FOOTER ? 'with-footer' : 'without-footer';

        return (
            <>
                {
                    this.props._isLoginPending ? <div className="spinner-overlay">
                        <Spinner size={'large'} />
                    </div> : null
                }
                <div id='login-popup-custom'>
                    <ModalTransition>
                        {this.state.showLoginPage && (
                            <Modal
                                show={this.state.showLoginPage}
                                onClose={this.onModalClose}
                                width={'medium'}
                            >
                                <LoginPage
                                    {...this.props}
                                    onJoin={this._onJoin}
                                    room={this.state.room}
                                    loginSuccessCallback={
                                        this.loginSuccessCallback
                                    }
                                    onModalClose={this.onModalClose}
                                ></LoginPage>
                            </Modal>
                        )}
                    </ModalTransition>
                </div>
                <div
                    className={`welcome ${contentClassName} ${footerClassName}`}
                    id='welcome_page'>
                    <div className="header-main">
                        <div className="logo">
                            <Watermarks
                                defaultJitsiLogoURL={DEFAULT_WELCOME_PAGE_LOGO_URL}
                                noMargins={true}
                            />
                        </div>
                        <div className="header-right">
                            <div className="head-right">
                                <div className="welcome-page-menu">
                                    <button onClick={this.onMenuClick}>
                                        <img src="../../../../images/dotsmenu.png" />
                                    </button>
                                    <div className="wel-menu-list">
                                        <ul>
                                            <li>
                                                <a href="https://www.blueskyplan.com/" target="_blank">
                                                    <i><img src="../../../../images/dash-plan.png" /></i>
                                                    <p>Plan</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://labpronto.com/" target="_blank">
                                                    <i><img src="../../../../images/dash-pronto.png" /></i>
                                                    <p>Pronto</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://biobigbox.com/" target="_blank">
                                                    <i><img src="../../../../images/dash-box.png" /></i>
                                                    <p>Box</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://isimulation.ismile.app/" target="_blank">
                                                    <i><img src="../../../../images/dash-simulation.png" /></i>
                                                    <p>Simulation</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://www.blueskybio.university/" target="_blank">
                                                    <i><img src="../../../../images/dash-university.png" /></i>
                                                    <p>University</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://blueskymonitoring.com/" target="_blank">
                                                    <i><img src="../../../../images/dash-monitoring.png" /></i>
                                                    <p>Monitoring</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://bsblogin.com/Identity/Account/Manage" target="_blank">
                                                    <i><img src="../../../../images/dash-account.png" /></i>
                                                    <p>Account</p>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://blueskybio.com/" target="_blank">
                                                    <i><img src="../../../../images/dash-bio.png" /></i>
                                                    <p>BlueSkyBio</p>
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div className="welcom-initial">
                                    <SettingsButton
                                        defaultTab={SETTINGS_TABS.CALENDAR}
                                    />
                                    {showAdditionalToolbarContent ? (
                                        <div
                                            className="settings-toolbar-content"
                                            ref={
                                                this._setAdditionalToolbarContentRef
                                            }
                                        />
                                    ) : null}
                                </div>
                                <div>
                                    {this.isUserLoggedIn() ? (
                                        <>
                                            <button className="welcom-initial" style={{ margin: '0' }} onClick={this.onUserIconClick}>
                                                {_settings.displayName
                                                    ? _settings.displayName
                                                        .charAt(0)
                                                        .toUpperCase()
                                                    : this.getUserName()
                                                        .charAt(0)
                                                        .toUpperCase()}
                                            </button>
                                            <div className="user-action">
                                                <ul>
                                                    <li>
                                                        <span>{this.isUserLoggedIn() && _settings.email
                                                            ? _settings.email
                                                            : this.getUserName()}</span>
                                                    </li>
                                                    <li className="header-login-btn">
                                                        <button
                                                            onClick={
                                                                this.isUserLoggedIn()
                                                                    ? this.onSSOLogoutClick
                                                                    : this.onSSOButtonClick
                                                            }
                                                        >
                                                            {this.state.loginButtonText}
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </>
                                    ) :
                                        <div className="header-login-btn">
                                            <button className="login-button"
                                                onClick={
                                                    this.isUserLoggedIn()
                                                        ? this.onSSOLogoutClick
                                                        : this.onSSOButtonClick
                                                }
                                            >
                                                <i>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-person" viewBox="0 0 16 16">
                                                        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                                                    </svg>
                                                </i>
                                                <span>{this.state.loginButtonText}</span>
                                            </button>
                                        </div>
                                    }
                                </div>
                            </div>
                            {/* <button
                                className="toogle-btn"
                                onClick={this.toggleMenu}
                            >
                                <span></span>
                            </button> */}
                        </div>
                    </div>
                    <div className='header'>
                        <div className='header-image' />
                        <div className='header-container'>
                            {/* <div className='header-watermark-container'>
                                <div className='welcome-watermark'>
                                    <Watermarks
                                        defaultJitsiLogoURL={DEFAULT_WELCOME_PAGE_LOGO_URL}
                                        noMargins={true} />
                                </div>
                            </div> */}
                            {/* <div className='welcome-page-settings'>
                                <button
                                    className="toogle-btn"
                                    onClick={this.toggleMenu}
                                >
                                    <span></span>
                                </button>
                                <SettingsButton
                                    defaultTab={SETTINGS_TABS.CALENDAR}
                                    isDisplayedOnWelcomePage={true} />
                                {showAdditionalToolbarContent
                                    ? <div
                                        className='settings-toolbar-content'
                                        ref={this._setAdditionalToolbarContentRef} />
                                    : null
                                }
                            </div> */}
                            {/* <div className='header-login-btn'>
                                <button
                                    onClick={
                                        this.isUserLoggedIn()
                                            ? this.onSSOLogoutClick
                                            : this.onSSOButtonClick
                                    }
                                >
                                    {this.state.loginButtonText}
                                </button>
                            </div> */}
                            {/* {this.isUserLoggedIn() && (
                                <div className='welcom-initial'>
                                    {_settings.displayName
                                        ? _settings.displayName
                                            .charAt(0)
                                            .toUpperCase()
                                        : this.getUserName()
                                            .charAt(0)
                                            .toUpperCase()}
                                </div>
                            )} */}
                            <h1 className='header-text-title'>
                                {t('welcomepage.headerTitle')}
                            </h1>
                            <span className='header-text-subtitle'>
                                {t('welcomepage.headerSubtitle')}
                            </span>
                            <div id='enter_room'>
                                <div className='enter-room-input-container'>
                                    <form onSubmit={this._onFormSubmit}>
                                        <input
                                            aria-disabled='false'
                                            aria-label='Meeting name input'
                                            autoFocus={true}
                                            className='enter-room-input'
                                            id='enter_room_field'
                                            onChange={this._onRoomChange}
                                            required
                                            pattern={ROOM_NAME_VALIDATE_PATTERN_STR}
                                            // placeholder = { this.state.roomPlaceholder }
                                            placeholder={t('welcomepage.meetingPlaceholder')}
                                            ref={this._setRoomInputRef}
                                            title={t('welcomepage.roomNameAllowedChars')}
                                            type='text'
                                            value={this.state.room} />
                                        <div
                                            className={_moderatedRoomServiceUrl
                                                ? 'warning-with-link'
                                                : 'warning-without-link'}>
                                            {this._renderInsecureRoomNameWarning()}
                                        </div>
                                    </form>
                                </div>
                                <button
                                    aria-disabled='false'
                                    aria-label='Start meeting'
                                    className='welcome-page-button'
                                    id='enter_room_button'
                                    onClick={this._onFormSubmit}
                                    tabIndex='0'
                                    type='button'>
                                    {t('welcomepage.startMeeting')}
                                </button>
                            </div>
                            <div className='conference-demo'>
                                <img src='/images/BlueSkyMeet_demo_image.png' />
                            </div>
                            {_moderatedRoomServiceUrl && (
                                <div id='moderated-meetings'>
                                    {
                                        translateToHTML(
                                            t, 'welcomepage.moderatedMessage', { url: _moderatedRoomServiceUrl })
                                    }
                                </div>)}
                        </div>
                    </div>

                    <div className='welcome-cards-container'>
                        <div className='welcome-card-column'>
                            <div className='welcome-tabs welcome-card welcome-card--blue'>
                                {this._renderTabs()}
                            </div>
                            {showAdditionalCard
                                ? <div
                                    className='welcome-card welcome-card--dark'
                                    ref={this._setAdditionalCardRef} />
                                : null}
                        </div>

                        {showAdditionalContent
                            ? <div
                                className='welcome-page-content'
                                ref={this._setAdditionalContentRef} />
                            : null}
                    </div>
                    {DISPLAY_WELCOME_FOOTER && this._renderFooter()}
                </div>
            </>

        );
    }

    /**
     * Renders the insecure room name warning.
     *
     * @inheritdoc
     */
    _doRenderInsecureRoomNameWarning() {
        return (
            <div className='insecure-room-name-warning'>
                <Icon src={IconWarning} />
                <span>
                    {this.props.t('security.insecureRoomNameWarning')}
                </span>
            </div>
        );
    }

    /**
     * Prevents submission of the form and delegates join logic.
     *
     * @param {Event} event - The HTML Event which details the form submission.
     * @private
     * @returns {void}
     */
    _onFormSubmit(event) {
        event.preventDefault();
        const { _ssoConf } = this.props;

        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
            // We will not directly allow user to join the room
            if (this.state.room && !this.isUserLoggedIn()) {
                this._onJoin();
                // this.setState({ showLoginPage: true });
            } else {
                let requestBody = {
                    user_token: jitsiLocalStorage.getItem('user_token'),
                    room_name: this.state.room,
                    ssoConfRedirect_uri: _ssoConf.redirect_uri
                };
                getJwt(requestBody);
            }
        }
    }

    /**
     * Overrides the super to account for the differences in the argument types
     * provided by HTML and React Native text inputs.
     *
     * @inheritdoc
     * @override
     * @param {Event} event - The (HTML) Event which details the change such as
     * the EventTarget.
     * @protected
     */
    _onRoomChange(event) {
        super._onRoomChange(event.target.value);
    }

    /**
     * Callback invoked when the desired tab to display should be changed.
     *
     * @param {number} tabIndex - The index of the tab within the array of
     * displayed tabs.
     * @private
     * @returns {void}
     */
    _onTabSelected(tabIndex) {
        this.setState({ selectedTab: tabIndex });
    }

    /**
     * Renders the footer.
     *
     * @returns {ReactElement}
     */
    _renderFooter() {
        const {
            t,
            _deeplinkingCfg: {
                ios = {},
                android = {}
            }
        } = this.props;

        const { downloadLink: iosDownloadLink } = ios;

        const { fDroidUrl, downloadLink: androidDownloadLink } = android;

        return (<footer className='welcome-footer'>
            {/* <div className = 'welcome-footer-centered'>
                <div className = 'welcome-footer-padded'>
                    <div className = 'welcome-footer-row-block welcome-footer--row-1'>
                        <div className = 'welcome-footer-row-1-text'>{t('welcomepage.jitsiOnMobile')}</div>
                        <a
                            className = 'welcome-badge'
                            href = { iosDownloadLink }>
                            <img
                                alt = { t('welcomepage.mobileDownLoadLinkIos') }
                                src = './images/app-store-badge.png' />
                        </a>
                        <a
                            className = 'welcome-badge'
                            href = { androidDownloadLink }>
                            <img
                                alt = { t('welcomepage.mobileDownLoadLinkAndroid') }
                                src = './images/google-play-badge.png' />
                        </a>
                        <a
                            className = 'welcome-badge'
                            href = { fDroidUrl }>
                            <img
                                alt = { t('welcomepage.mobileDownLoadLinkFDroid') }
                                src = './images/f-droid-badge.png' />
                        </a>
                    </div>
                </div>
            </div> */}
            <div className='footer_links'>
                <a href='/help.html' target='_blank'>
                    BlueSkyMeet Functionality Explained
                </a>
                <a href='https://www.google.com/chrome/' target='_blank'>
                    Download Google Chrome Browser
                </a>
                <a
                    href='https://www.microsoft.com/en-us/edge'
                    target='_blank'
                >
                    Download Latest Microsoft Edge Browser
                </a>
            </div>
            <center>
                By Using BlueSkyMeet.com I hereby agree to the{' '}
                <a href='/toc.html' target='_blank'>
                    Terms and Conditions
                </a>{' '}
                of the site.
            </center>
        </footer>
        );
    }

    /**
     * Renders tabs to show previous meetings and upcoming calendar events. The
     * tabs are purposefully hidden on mobile browsers.
     *
     * @returns {ReactElement|null}
     */
    _renderTabs() {
        if (isMobileBrowser()) {
            return null;
        }

        const { _calendarEnabled, _recentListEnabled, t } = this.props;

        const tabs = [];

        if (_calendarEnabled) {
            tabs.push({
                label: t('welcomepage.calendar'),
                content: <CalendarList />
            });
        }

        if (_recentListEnabled) {
            tabs.push({
                label: t('welcomepage.recentList'),
                content: <RecentList />
            });
        }

        if (tabs.length === 0) {
            return null;
        }

        return (
            <Tabs
                onSelect={this._onTabSelected}
                selected={this.state.selectedTab}
                tabs={tabs} />
        );
    }

    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * additional card shown near the tabs card.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalCardRef(el) {
        this._additionalCardRef = el;
    }

    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * welcome page content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalContentRef(el) {
        this._additionalContentRef = el;
    }

    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * toolbar additional content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the additional toolbar content.
     * @private
     * @returns {void}
     */
    _setAdditionalToolbarContentRef(el) {
        this._additionalToolbarContentRef = el;
    }

    /**
     * Sets the internal reference to the HTMLInputElement used to hold the
     * welcome page input room element.
     *
     * @param {HTMLInputElement} el - The HTMLElement for the input of the room name on the welcome page.
     * @private
     * @returns {void}
     */
    _setRoomInputRef(el) {
        this._roomInputRef = el;
    }

    /**
     * Returns whether or not an additional card should be displayed near the tabs.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalCard() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD
            && this._additionalCardTemplate
            && this._additionalCardTemplate.content
            && this._additionalCardTemplate.innerHTML.trim();
    }

    /**
     * Returns whether or not additional content should be displayed below
     * the welcome page's header for entering a room name.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT
            && this._additionalContentTemplate
            && this._additionalContentTemplate.content
            && this._additionalContentTemplate.innerHTML.trim();
    }

    /**
     * Returns whether or not additional content should be displayed inside
     * the header toolbar.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalToolbarContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT
            && this._additionalToolbarContentTemplate
            && this._additionalToolbarContentTemplate.content
            && this._additionalToolbarContentTemplate.innerHTML.trim();
    }
}

export default translate(connect(_mapStateToProps)(WelcomePage));
